import { useQuery, useSuspenseQuery } from '@apollo/client';
import { useReactTable } from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  Button,
  ColDefBuilder,
  DataTableFilterField,
  Icon,
  ServerDataTable,
  SimpleTooltip,
  Stack,
  TableSearch,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useDataTableForQuery,
} from '@eluve/components';
import {
  LlmOutputTypesLookup,
  PromptTemplatesBoolExp,
  PromptTemplatesOrderBy,
} from '@eluve/graphql-types';
import { ResultOf } from '@eluve/graphql.tada';

import {
  GlobalBundleBlocker,
  groupGlobalBlockersByFk,
} from './GlobalBundleBlockerTooltip';
import { TogglePromptTemplateActiveAction } from './TogglePromptTemplateActiveAction';
import {
  allModeArgsQuery,
  getActiveBundleBlockersQuery,
  searchPromptTemplatesQuery,
} from './prompt.operations';

type PromptTemplateRow = {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  isCurrentDefault: boolean;
  createdAt: string;
  hasDbTemplate: boolean;
  usersWithDefault: number;
  variantCount: number;
  feedbackCount: number;
  averageRating: number | null;
  modelArgsFormatted: {
    name: string;
    modelType: string;
    args: Record<string, unknown>;
    isDefault: boolean;
  }[];
  /** Attached per-page from `getActiveBundleBlockersQuery`, not the row query. */
  blockers?: GlobalBundleBlocker[];
};

const columns = new ColDefBuilder<PromptTemplateRow>()
  .linkSortable('name', (row) => `./${row.id}`)
  .defaultSortable('type')
  .defaultBoolean('isCurrentDefault', 'Is Default')
  .defaultBoolean('isActive', 'Active')
  .dateSortable('createdAt', 'Created At')
  .defaultSortable('feedbackCount', '# Feedback')
  .defaultSortable('averageRating', 'Avg Rating')
  .defaultSortable('variantCount', '# Variants')
  .defaultSortable('usersWithDefault', '# User Default')
  .colDef({
    id: 'modelArgsFormatted',
    accessorKey: 'modelArgsFormatted',
    header: 'Model Type',
    cell: ({ row }) => {
      return (
        <Stack wrap gap={1} direction="horizontal">
          {row.original.modelArgsFormatted.map((modelArgs, index) => (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <span>
                  <Tag
                    className="font-semibold"
                    size="s"
                    pill
                    color={modelArgs.isDefault ? 'positive' : 'gray'}
                    label={modelArgs.name}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <pre className="text-xs whitespace-pre">
                  {`${modelArgs.modelType}\n${JSON.stringify(modelArgs.args, null, 2)}`}
                </pre>
              </TooltipContent>
            </Tooltip>
          ))}
        </Stack>
      );
    },
  })
  .colDef({
    header: 'Actions',
    cell: ({ row }) => {
      const showCloneOption =
        row.original.hasDbTemplate && row.original.isActive;

      return (
        <Stack direction="horizontal" fullWidth={false} gap={1}>
          <TogglePromptTemplateActiveAction
            promptTemplateId={row.original.id}
            blockers={row.original.blockers}
          />
          {showCloneOption && (
            <SimpleTooltip
              label={`Create a new Prompt Template using '${row.original.name}' as a starting point`}
            >
              <Link to={`./clone/${row.original.id}`}>
                <Button
                  variant="outline"
                  aria-label={`Clone ${row.original.name}`}
                >
                  <Icon name="CopyPlus" size="xl" />
                </Button>
              </Link>
            </SimpleTooltip>
          )}
        </Stack>
      );
    },
  })
  .build();

const convertSearchResultsToRows = (
  results: ResultOf<typeof searchPromptTemplatesQuery>,
): PromptTemplateRow[] => {
  return results.promptTemplates.map<PromptTemplateRow>((p) => {
    const currentDefaultVariant = p.prompt_template_variants.find(
      (variant) => variant.isCurrentDefault,
    );

    const allModelArgs = p.prompt_models
      .filter((model) => model.isActive !== false)
      .map((model) => ({
        name: model.model_args.name ?? model.model_args.modelType,
        modelType: model.model_args.modelType,
        args: model.model_args.args as Record<string, unknown>,
        isDefault: model.isDefault,
      }))
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));

    return {
      id: p.id,
      name: p.name,
      type: p.outputType,
      createdAt: p.createdAt,
      isActive: p.isActive ?? false,
      isCurrentDefault: p.isCurrentDefault,
      hasDbTemplate: !isNil(currentDefaultVariant?.template),
      usersWithDefault: p.metrics?.userDefaultCount ?? 0,
      variantCount: p.metrics?.variantCount ?? 0,
      feedbackCount: p.metrics?.totalFeedbackCount ?? 0,
      averageRating:
        p.metrics?.averageRating != null
          ? Math.round(p.metrics.averageRating * 100) / 100
          : null,
      modelArgsFormatted: allModelArgs,
    };
  });
};

export const PromptTemplatesList: React.FC = () => {
  const { data: modelArgsList } = useQuery(allModeArgsQuery);

  const {
    data: { llmModelPromptTemplates: blockerRows },
  } = useSuspenseQuery(getActiveBundleBlockersQuery);

  const blockersByPromptTemplateId = useMemo(
    () => groupGlobalBlockersByFk(blockerRows, (row) => row.promptTemplateId),
    [blockerRows],
  );

  const modelArgFilters =
    modelArgsList?.llmModelArgs.map((arg) => {
      return {
        label: `${arg.modelType}${arg.name ? ' - ' + arg.name : ''}`,
        value: arg.id,
      };
    }) ?? [];

  const filterFields: DataTableFilterField<PromptTemplateRow>[] = [
    {
      id: 'type',
      label: 'Output Type',
      options: Object.keys(LlmOutputTypesLookup)
        .sort()
        .map((type) => ({
          label: type,
          value: type,
        })),
    },
    {
      id: 'modelArgsFormatted',
      label: 'Enabled Model Types',
      options: modelArgFilters.map((arg) => ({
        label: arg.label,
        value: arg.value,
      })),
    },
    {
      id: 'isActive',
      label: 'Active',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
  ];

  const { data, rows, setSearch, search, reactTableOptions, isPending } =
    useDataTableForQuery({
      query: searchPromptTemplatesQuery,
      filterFields,
      convertToRows: convertSearchResultsToRows,
      convertSearchParamsToVariables: ({
        offset,
        limit,
        search,
        sorting,
        filters,
      }) => {
        const filterConditions: PromptTemplatesBoolExp[] = [];

        if (filters.type) {
          filterConditions.push({
            outputType: {
              _in: filters.type as (keyof typeof LlmOutputTypesLookup)[],
            },
          });
        }

        if (filters.isActive) {
          filterConditions.push({
            isActive: { _eq: filters.isActive[0] === 'true' },
          });
        }
        if (filters.modelArgsFormatted) {
          filterConditions.push({
            prompt_models: {
              model_args: {
                id: {
                  _in: [...filters.modelArgsFormatted],
                },
              },
            },
          });
        }

        if (search) {
          filterConditions.push({
            name: {
              _ilike: `%${search}%`,
            },
          });
        }

        return {
          offset,
          limit,
          filter: {
            _and: filterConditions,
          },
          orderBy:
            sorting && sorting.length
              ? sorting.map(({ desc, id }) => ({
                  ...match<keyof PromptTemplateRow, PromptTemplatesOrderBy>(id)
                    .with('type', () => ({ outputType: desc ? 'DESC' : 'ASC' }))
                    .with('usersWithDefault', () => ({
                      metrics: { userDefaultCount: desc ? 'DESC' : 'ASC' },
                    }))
                    .with('variantCount', () => ({
                      metrics: { variantCount: desc ? 'DESC' : 'ASC' },
                    }))
                    .with('feedbackCount', () => ({
                      metrics: { totalFeedbackCount: desc ? 'DESC' : 'ASC' },
                    }))
                    .with('averageRating', () => ({
                      metrics: {
                        averageRating: desc
                          ? 'DESC_NULLS_LAST'
                          : 'ASC_NULLS_LAST',
                      },
                    }))
                    .otherwise(() => ({
                      [id]: desc ? 'DESC' : 'ASC',
                    })),
                }))
              : null,
        };
      },
    });

  const rowCount = data?.promptTemplatesAggregate?.aggregate?.count;

  const rowsWithBlockers = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        blockers: blockersByPromptTemplateId.get(row.id) ?? [],
      })),
    [rows, blockersByPromptTemplateId],
  );

  const table = useReactTable({
    data: rowsWithBlockers,
    columns,
    rowCount,
    ...reactTableOptions,
  });

  return (
    <Stack gap={2} className="rounded-md border">
      <TableSearch
        value={search ?? ''}
        onChange={setSearch}
        placeholder="Search by name..."
      />
      <ServerDataTable<PromptTemplateRow>
        table={table}
        filterFields={filterFields}
        isPending={isPending}
      />
    </Stack>
  );
};
